import { courier } from '../../../../core/services/courier.service';
import { BACKGROUND_NETWORK_REQ_OPTIONS } from '../../../../core/constants/constants';
import { GenericApiResponse } from '../../../../edge/shared/interfaces/generics';
import { IWorkspace, IWorkspaceMember } from '../interfaces/workspace';

const WORKSPACES_URL = `/api/security/v2.0/workspaces`;
const USERS_URL = `/api/security/v2.0/users`;
const AUTH_URL = `/api/security/v2.0/auth`;

function handleResponse<T>(resp: GenericApiResponse<T>): GenericApiResponse<T> {
  if (resp?.success) {
    return resp;
  }
  throw resp;
}

function handleNetworkError(err: any): never {
  if (err && (err.error_message !== undefined || err.success !== undefined)) {
    throw err;
  }
  if (err?.error && (err.error.error_message !== undefined || err.error.success !== undefined)) {
    throw err.error;
  }
  const error = new Error(
    err?.message || 'A network error occurred. Please check your connection and try again.'
  );
  Object.assign(error, { success: false, error_code: err?.status || 0 });
  throw error;
}

export const WorkspacesAPI = {
  getAllWorkspaces(): Promise<GenericApiResponse<IWorkspace[]>> {
    return courier
      .get(WORKSPACES_URL, BACKGROUND_NETWORK_REQ_OPTIONS)
      .then(resp => handleResponse<IWorkspace[]>(resp))
      .catch(handleNetworkError);
  },

  getWorkspace(workspaceId: string): Promise<GenericApiResponse<IWorkspace>> {
    return courier
      .get(`${WORKSPACES_URL}/${workspaceId}`, BACKGROUND_NETWORK_REQ_OPTIONS)
      .then(resp => handleResponse<IWorkspace>(resp))
      .catch(handleNetworkError);
  },

  createWorkspace(payload: { name: string }): Promise<GenericApiResponse<IWorkspace>> {
    return courier
      .post(WORKSPACES_URL, BACKGROUND_NETWORK_REQ_OPTIONS, payload)
      .then(resp => handleResponse<IWorkspace>(resp))
      .catch(handleNetworkError);
  },

  updateWorkspace(
    workspaceId: string,
    payload: { name: string }
  ): Promise<GenericApiResponse<IWorkspace>> {
    return courier
      .put(`${WORKSPACES_URL}/${workspaceId}`, BACKGROUND_NETWORK_REQ_OPTIONS, payload)
      .then(resp => handleResponse<IWorkspace>(resp))
      .catch(handleNetworkError);
  },

  deleteWorkspace(workspaceId: string): Promise<GenericApiResponse<null>> {
    return courier
      .delete(`${WORKSPACES_URL}/${workspaceId}`, BACKGROUND_NETWORK_REQ_OPTIONS)
      .then(resp => handleResponse<null>(resp))
      .catch(handleNetworkError);
  },

  getWorkspaceMembers(workspaceId: string): Promise<GenericApiResponse<IWorkspaceMember[]>> {
    return courier
      .get(`${WORKSPACES_URL}/${workspaceId}/users`, BACKGROUND_NETWORK_REQ_OPTIONS)
      .then(resp => handleResponse<IWorkspaceMember[]>(resp))
      .catch(handleNetworkError);
  },

  inviteWorkspaceMembers(payload: {
    workspace_id: string;
    emails: string[];
    rbac: { roles: string[] };
  }): Promise<GenericApiResponse<null>> {
    // 201 with empty body — treat any resolved 2xx as success
    return courier
      .post(`${USERS_URL}/invite`, BACKGROUND_NETWORK_REQ_OPTIONS, payload)
      .then(
        () =>
          ({
            data: null,
            success: true,
            count: payload.emails.length,
            success_message: `Invite sent to ${payload.emails.length} user(s)`
          } as GenericApiResponse<null>)
      )
      .catch(handleNetworkError);
  },

  updateWorkspaceMemberRoles(
    workspaceId: string,
    userId: number,
    roles: string[]
  ): Promise<GenericApiResponse<null>> {
    return courier
      .put(
        `${WORKSPACES_URL}/${workspaceId}/users/${userId}/roles`,
        BACKGROUND_NETWORK_REQ_OPTIONS,
        { roles }
      )
      .then(resp => handleResponse<null>(resp))
      .catch(handleNetworkError);
  },

  selectTeam(teamId: number): Promise<GenericApiResponse<{ session_token: string }>> {
    return courier
      .post(`${AUTH_URL}/select-team`, BACKGROUND_NETWORK_REQ_OPTIONS, { team_id: teamId })
      .then(resp => handleResponse<{ session_token: string }>(resp))
      .catch(handleNetworkError);
  },

  removeWorkspaceMember(workspaceId: string, userId: number): Promise<GenericApiResponse<null>> {
    return courier
      .delete(`${WORKSPACES_URL}/${workspaceId}/users/${userId}`, BACKGROUND_NETWORK_REQ_OPTIONS)
      .then(resp => handleResponse<null>(resp))
      .catch(handleNetworkError);
  }
};
